import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { readProlificParams, rememberProlificSource } from "@/lib/prolific";

/**
 * /tester/:code — one-click sign-in for User Interviews participants.
 * Each code maps to a clean approved account with its own session.
 *
 * Reads `?iid={{iid}}` (User Interviews' standard variable) — falls back to
 * `?participant_id={{UID}}` for older links — and stashes it everywhere we
 * need it later:
 *   • sent to the backend so the admin dashboard knows which research
 *     participant this account belongs to,
 *   • saved to localStorage so the dashboard "Finish study" CTA and the
 *     feedback submission can redirect them back to User Interviews when
 *     they're done.
 */
export default function TesterLogin() {
    const { code } = useParams();
    const location = useLocation();
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [error, setError] = useState(null);

    useEffect(() => {
        const run = async () => {
            try {
                const params = new URLSearchParams(location.search);
                // Prolific passes PROLIFIC_PID + STUDY_ID + SESSION_ID. If we
                // see them, treat PROLIFIC_PID as the iid and remember the
                // source so /study-complete can show the Prolific completion
                // code on the way out.
                const prolific = readProlificParams(location.search);
                if (prolific.pid) {
                    rememberProlificSource({ studyId: prolific.studyId, sessionId: prolific.sessionId });
                }
                const iid = (
                    params.get("iid")
                    || params.get("participant_id")
                    || prolific.pid
                    || ""
                ).trim();
                if (iid) {
                    // sessionStorage = "live across this tab", localStorage = "survives reload + nav".
                    // We use both so the Finish-study CTA + feedback redirect work even after
                    // a fresh browser tab is opened for any reason.
                    try {
                        sessionStorage.setItem("mlcn_participant_id", iid);
                        localStorage.setItem("mlcn_participant_id", iid);
                    } catch (_e) { /* noop */ }
                }

                // Two flows:
                //  1. Admin-generated /tester/:code  → exchange that code (iid optional).
                //  2. User Interviews single-base-URL /tester/?iid=...  → no code, iid required.
                let r;
                const source = prolific.pid ? "prolific" : "";
                if (code) {
                    r = await api.post(
                        `/auth/tester-login/${encodeURIComponent(code)}`,
                        iid ? { participant_id: iid, source: source || undefined } : { source: source || undefined },
                    );
                } else if (iid) {
                    r = await api.post("/auth/tester-by-iid", {
                        participant_id: iid,
                        ...(source ? { source } : {}),
                        // Pass STUDY_ID + SESSION_ID through so the backend
                        // can write them on the user record + on every
                        // prolific_events log entry. This is what makes
                        // "which duplicated Prolific study did this
                        // participant come from?" answerable from /admin.
                        ...(prolific.studyId ? { prolific_study_id: prolific.studyId } : {}),
                        ...(prolific.sessionId ? { prolific_session_id: prolific.sessionId } : {}),
                    });
                } else {
                    setError("This tester link is missing its id. Please check the URL.");
                    return;
                }
                setToken(r.data.token);
                if (r.data.user?.primary_session_id) {
                    localStorage.setItem("mlcn_session_id", r.data.user.primary_session_id);
                }
                await refresh();
                // Route by screener + source. Only Prolific testers go through
                // the screener; one-off iid links from Reddit/email/etc. skip
                // straight to /dashboard since the founder has already vetted
                // those testers manually.
                const u = r.data.user || {};
                const isProlific = u.tester_source === "prolific";
                if (isProlific && !u.screener?.completed_at) {
                    navigate("/screener", { replace: true });
                } else if (isProlific && u.screener && !u.screener.qualified) {
                    navigate("/screener", { replace: true });
                } else {
                    toast.success("Welcome — take Memory Lane for a spin.");
                    navigate("/dashboard");
                }
            } catch (err) {
                setError(err?.response?.data?.detail || "That tester link isn't valid.");
            }
        };
        run();
    }, [code, location.search, navigate, refresh]);

    return (
        <div className="min-h-screen bg-cream flex items-center justify-center p-6">
            <div className="max-w-md w-full text-center">
                <div className="flex items-center justify-center gap-2 text-teal-dark mb-6">
                    <Heart strokeWidth={1.6} size={20} />
                    <span className="text-sm uppercase tracking-wider font-semibold">Memory Lane Care Navigator</span>
                </div>
                {!error && (
                    <div className="ml-card p-8" data-testid="tester-loading">
                        <Loader2 className="mx-auto animate-spin text-teal mb-3" />
                        <p className="text-[#4C5551]">Setting up your tester account…</p>
                    </div>
                )}
                {error && (
                    <div className="ml-card p-8" data-testid="tester-error">
                        <h1 className="text-2xl font-heading font-medium mb-2">Hmm.</h1>
                        <p className="text-[#4C5551]">{error}</p>
                        <p className="text-xs text-[#6E7A74] mt-3">If you&apos;re a research participant, double-check the link you were given.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
